import { useState, useEffect } from "react"
import { motion } from "framer-motion"
import whiteSVG from "../assets/images/WhiteBackground.png"

const PrivacyPolicy = () => {
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640)
    }
    checkMobile()
    window.addEventListener("resize", checkMobile)
    return () => window.removeEventListener("resize", checkMobile)
  }, [])

  return (
    <div className="bg-white">
      <motion.div
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1 }}
        style={{
          backgroundImage: `url(${whiteSVG})`,
          backgroundSize: isMobile ? "cover" : "100% 100%",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          padding: "10px",
          marginTop: "-100px",
          paddingTop: "200px",
          paddingLeft: isMobile ? "20px" : "220px",
          paddingRight: isMobile ? "20px" : "300px",
          paddingBottom: "200px",
        }}
        className="font-bold text-4xl sm:text-5xl text-center sm:text-left mb-12"
      >
        Privacy Policy
      </motion.div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
          className="bg-white shadow-lg rounded-2xl p-6 sm:p-8"
        >
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 sm:mb-6 text-center">
            Privacy Policy for Lightning Technologies
          </h2>
          <p className="text-gray-700 leading-relaxed text-base sm:text-lg text-justify mb-4 sm:mb-6">
            <span className="font-semibold">Effective Date: 18 Oct 2023</span>
          </p>
          <div className="space-y-4 sm:space-y-6">
            <p className="text-gray-700 leading-relaxed text-sm sm:text-base text-justify break-words">
              At <span className="text-black font-semibold">Lightning Technologies</span>, your privacy is of utmost importance to us.
              This Privacy Policy explains how we collect, use, share, and protect your personal data when you interact
              with our website and use our digital product, MarketMind, a web-based ERP system designed for commission
              agents, businesses, and supply chain stakeholders. By using our services, you agree to the practices
              outlined in this policy. This policy complies with the relevant laws and regulations, including India's{" "}
              <span className="text-black font-semibold">Information Technology Act, 2000,</span> and{" "}
              <span className="text-black font-semibold">IT Rules, 2021</span>, as well as global privacy standards where applicable.
            </p>

            <p className="text-gray-700 leading-relaxed text-sm sm:text-base text-justify break-words">
              We collect various types of information to enhance your experience with our services and ensure smooth
              operations of our web platform. When you create an account, we collect personal details such as your full
              name, email address, phone number, and address. During the registration process for business purposes, we
              may collect additional information, including your firm name, business type, GSTIN, and other relevant
              business registration details. This information helps us manage your account and provide a tailored
              experience on <span className="text-black font-semibold">MarketMind</span>.
            </p>

            <p className="text-gray-700 leading-relaxed text-sm sm:text-base text-justify break-words">
              To ensure smooth transactions, we also collect payment-related information during product or subscription
              purchases. While we process payments through trusted third-party gateways such as Razorpay, we may collect
              and store information such as your billing address, transaction history, and payment method details.
              However, sensitive payment details such as your card number or UPI ID are handled directly by the payment
              provider and not stored on our servers. We also collect data related to how you interact with our
              platform, including your IP address, browser type, device information, pages visited, and activity logs.
              This usage data helps us improve the performance, security, and usability of our services.
            </p>

            <p className="text-gray-700 leading-relaxed text-sm sm:text-base text-justify break-words">
              Cookies and similar tracking technologies play a crucial role in enhancing your browsing experience. These
              technologies allow us to remember your preferences, secure your sessions, and analyze usage patterns to
              provide better services. You have the option to manage or disable cookies through your browser settings;
              however, please note that doing so may affect the functionality of certain features on our platform.
            </p>

            <p className="text-gray-700 leading-relaxed text-sm sm:text-base text-justify break-words">
              We use your data to provide core functionalities, such as account management, secure access to our
              services, and customer support. Your data helps us deliver an efficient and personalized experience by
              allowing us to recommend features relevant to your business needs. Payment data is used solely to process
              transactions and comply with legal and financial regulations. We may also use your contact information to
              send you service updates, announcements, or promotional offers, but only with your explicit consent. You
              can opt out of marketing communications at any time by adjusting your preferences in your account settings
              or contacting our support team.
            </p>

            <p className="text-gray-700 leading-relaxed text-sm sm:text-base text-justify break-words">
              While we do not sell or rent your data to third parties, we may share information with trusted service
              providers to facilitate services on our behalf. These providers include payment processors, cloud hosting
              services, analytics partners, and communication services. All such providers are contractually obligated
              to protect your data and use it only for the purposes specified in their agreements with us. Additionally,
              we may disclose your information if required by law, such as in response to a court order or legal
              request. In the event of a business merger, acquisition, or sale, your data may be transferred to the new
              entity, and we will notify you of any changes affecting your data privacy.
            </p>

            <p className="text-gray-700 leading-relaxed text-sm sm:text-base text-justify break-words">
              We prioritize the security of your data by implementing industry-standard measures such as encryption,
              access control, and regular security audits. Your password is encrypted, and sensitive data is secured to
              prevent unauthorized access. However, no online system is completely foolproof, and we encourage you to
              safeguard your login credentials and notify us immediately if you suspect any security breaches.
            </p>

            <p className="text-gray-700 leading-relaxed text-sm sm:text-base text-justify break-words">
              As a user of <span className="text-black font-semibold">MarketMind</span>, you have rights regarding your personal data.
              Depending on your location, you may have the right to access your data, request corrections to inaccurate
              information, and request the deletion of your data, subject to legal retention requirements. You can also
              withdraw consent for certain data processing activities, such as marketing communications. To exercise
              your rights or file complaints, please contact us at
              <span className="text-black font-semibold">privacy@lightningtechnologies.net</span>. Our designated{" "}
              <span className="text-black font-semibold">Grievance Officer</span> will address your concerns within the legally
              mandated timeframe.
            </p>

            <p className="text-gray-700 leading-relaxed text-sm sm:text-base text-justify break-words">
              We retain your data for as long as necessary to provide our services and comply with legal obligations.
              When you close your account, we may retain certain data for a limited period to fulfill regulatory or
              contractual requirements. After this period, your data is securely deleted or anonymized. If you interact
              with our platform from outside India, please note that your data may be transferred to and processed on
              servers located in India or other countries where our service providers operate. We take steps to ensure
              that cross-border data transfers comply with applicable data protection laws.
            </p>

            <p className="text-gray-700 leading-relaxed text-sm sm:text-base text-justify break-words">
              Our services are not intended for individuals under the age of 18. We do not knowingly collect personal
              data from children. If you believe that a child has provided us with personal information, please contact
              us, and we will take immediate steps to remove the data.
            </p>

            <p className="text-gray-700 leading-relaxed text-sm sm:text-base text-justify break-words">
              We may update this Privacy Policy periodically to reflect changes in our practices, services, or legal
              requirements. Significant updates will be communicated to you through email or in-app notifications. We
              encourage you to review this policy regularly to stay informed about how we protect your privacy.
            </p>

            <p className="text-gray-700 leading-relaxed text-sm sm:text-base text-justify break-words">
              If you have any questions, concerns, or requests regarding this Privacy Policy, you can reach us at the
              contact information below:
            </p>

            <div className="mt-6 sm:mt-8">
              <h3 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-2 sm:mb-3">Contact Information</h3>
              <p className="text-gray-700 text-sm sm:text-base leading-relaxed break-words">
                <span className="text-black font-semibold">Lightning Technologies</span>
                <br /> Email: <span className="text-black font-semibold">privacy@lightningtechnologies.net</span>
                <br /> Address:<span className="text-black font-semibold"> Pune, Maharashtra, India</span>
              </p>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="mt-8 sm:mt-12 p-4 sm:p-6 bg-[#BDE9FB] rounded-lg border border-gray-300 text-center"
          >
            <p className="text-xs sm:text-sm text-gray-700 italic">
              *We may update this Privacy Policy periodically to reflect changes in our practices, services, or legal
              requirements.*
            </p>
          </motion.div>
        </motion.div>
      </div>
    </div>
  )
}

export default PrivacyPolicy

